export type DrawerEntityType = "agency-visit" | "contact" | "document" | "travelers-check" | "agency-user" | "agency"

/**
 * Maps notification URL paths to drawer entities.
 * Handles both Yii2 routes (/entity/view?id=N) and pretty URLs (/entity/N).
 */
const URL_PATTERNS: { pattern: RegExp; entity: DrawerEntityType; idGroup: number }[] = [
  { pattern: /^\/agency[-_]?visit(?:s)?(?:\/|\?id=)(\d+)/i, entity: "agency-visit", idGroup: 1 },
  { pattern: /^\/agency[-_]?user(?:s)?(?:\/|\?id=)(\d+)/i, entity: "agency-user", idGroup: 1 },
  { pattern: /^\/contact(?:s)?(?:\/|\?id=)(\d+)/i, entity: "contact", idGroup: 1 },
  { pattern: /^\/document(?:s)?(?:\/|\?id=)(\d+)/i, entity: "document", idGroup: 1 },
  { pattern: /^\/travelers[-_]?check(?:s)?(?:\/|\?id=)(\d+)/i, entity: "travelers-check", idGroup: 1 },
  { pattern: /^\/agency(?:es)?(?:\/|\?id=)(\d+)/i, entity: "agency", idGroup: 1 },
]

const ENTITY_BASE_PATHS: Record<DrawerEntityType, string> = {
  "agency-visit": "/agency-visits",
  contact: "/contacts",
  document: "/documents",
  "travelers-check": "/travelers-check",
  "agency-user": "/agency-users",
  agency: "/agencies",
}

/**
 * Parses a notification URL and returns the entity type and id.
 */
export function parseNotificationUrl(url: string | undefined): { entity: DrawerEntityType; id: number } | null {
  if (!url) return null

  const pathname = url.replace(/^https?:\/\/[^/]+/, "")
  const queryIdx = pathname.indexOf("?")
  const basePath = queryIdx >= 0 ? pathname.slice(0, queryIdx) : pathname

  for (const { pattern, entity, idGroup } of URL_PATTERNS) {
    const match = basePath.match(pattern)
    if (!match) continue

    const id = Number(match[idGroup])
    if (Number.isNaN(id)) return null

    return { entity, id }
  }

  return null
}

/**
 * Translates a notification URL into the clean React route that will
 * trigger the right drawer. Falls back to the original URL if no drawer
 * mapping exists.
 */
export function buildDrawerNavigation(url: string | undefined): string | null {
  const parsed = parseNotificationUrl(url)
  if (!parsed) return null

  const base = ENTITY_BASE_PATHS[parsed.entity]
  if (!base) return null

  return `${base}/${parsed.id}`
}
